import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { GripVertical, Trash2, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { QuoteNoteLine } from "@forgepoint/shared";
import { MarkdownReact } from "@/components/MarkdownReact";
import { RichTextEditorSheet } from "@/components/RichTextEditorSheet";
import { useFeatureFlag } from "@/store/featureFlagStore";

interface NoteRowProps {
  id: string;
  lineNumber: number | null;
  line: QuoteNoteLine;
  readOnly: boolean;
  onUpdate: (line: QuoteNoteLine) => void;
  onRemove: () => void;
}

/**
 * Renders a note line in the quote items table with markdown preview and a sheet editor.
 * @param props - The note row props
 * @returns The note row component
 */
export function NoteRow({
  id,
  lineNumber,
  line,
  readOnly,
  onUpdate,
  onRemove,
}: NoteRowProps) {
  const showMargin = useFeatureFlag("quote-line-margin");
  const [editorOpen, setEditorOpen] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: readOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleDescriptionChange = (value: string) => {
    if (readOnly) {
      return;
    }
    onUpdate({ ...line, description: value });
  };

  return (
    <>
      <TableRow
        ref={setNodeRef}
        style={style}
        className={cn("group", isDragging && "opacity-50")}
      >
        {/* Drag Handle */}
        <TableCell className="w-8 pr-0 py-2 px-1 sm:px-2">
          {!readOnly && (
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing touch-manipulation p-1"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
        </TableCell>

        {/* Line Number */}
        <TableCell className="py-2 px-0 sm:px-1 text-center">
          {lineNumber !== null && (
            <span className="text-xs tabular-nums text-muted-foreground">
              {lineNumber}
            </span>
          )}
        </TableCell>

        {/* Note content spans multiple columns */}
        <TableCell colSpan={showMargin ? 6 : 5} className="py-2 px-1 sm:px-2">
          <div className="flex items-start gap-2 sm:gap-3">
            <Badge
              variant="secondary"
              className="text-xs sm:text-sm shrink-0 mt-0.5"
            >
              Note
            </Badge>
            {line.description ? (
              <div
                className={cn(
                  "flex-1 text-muted-foreground text-xs sm:text-sm prose prose-sm dark:prose-invert max-w-none",
                  "prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-1",
                  !readOnly && "cursor-pointer hover:bg-muted/50 rounded px-1",
                )}
                onClick={!readOnly ? () => setEditorOpen(true) : undefined}
              >
                <MarkdownReact>{line.description}</MarkdownReact>
              </div>
            ) : (
              <div
                className={cn(
                  "flex-1 text-muted-foreground/50 italic text-xs sm:text-sm px-2 py-1",
                  !readOnly && "cursor-pointer hover:bg-muted/50 rounded",
                )}
                onClick={!readOnly ? () => setEditorOpen(true) : undefined}
              >
                Enter note...
              </div>
            )}
          </div>
        </TableCell>

        {/* Actions */}
        <TableCell className="py-2 px-1 sm:px-2">
          {!readOnly && (
            <div className="flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 sm:h-8 sm:w-8 opacity-0 group-hover:opacity-100 transition-opacity touch-manipulation"
                onClick={() => setEditorOpen(true)}
              >
                <Pencil className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 sm:h-8 sm:w-8 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity touch-manipulation"
                onClick={onRemove}
              >
                <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            </div>
          )}
        </TableCell>
      </TableRow>

      <RichTextEditorSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        title="Edit Note"
        value={line.description}
        onChange={handleDescriptionChange}
        placeholder="Enter note content"
      />
    </>
  );
}
